import React from 'react';
import { CheckCircle, AlertTriangle, BookOpen, Clock, Users, ShieldCheck, Zap } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { TestimonialSection } from '../components/TestimonialSection';
import { BonusSection } from '../components/BonusSection';
import { PurchaseSection } from '../components/PurchaseSection';
import { FAQSection } from '../components/FAQSection';
export function LandingPage() {
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-235">
      <PurchaseNotification data-id="element-236" />

      {/* Hero Section */}
      <header className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white pt-8 pb-12 md:pt-12 md:pb-20 overflow-hidden" data-id="element-237">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-238"></div>

        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-239">
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-pulse" data-id="element-240"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl animate-pulse" style={{
          animationDelay: '1s'
        }} data-id="element-241"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-242">
          <div className="text-center max-w-5xl mx-auto" data-id="element-243">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-6 py-3 rounded-full text-sm font-bold mb-4 md:mb-6 shadow-2xl" data-id="element-244">
              <Zap className="w-4 h-4" data-id="element-245" />
              <span data-id="element-246">Para docentes de nivel secundario</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black mb-6 md:mb-8 leading-tight drop-shadow-2xl" data-id="element-247">
              Micro y Macro
              <br data-id="element-248" />
              Economía <span className="text-amber-400" data-id="element-249">360</span>
            </h1>

            {/* Cover Image */}
            <div className="relative max-w-[200px] sm:max-w-[250px] md:max-w-xs mx-auto mb-6 md:mb-8" data-id="element-250">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur-2xl opacity-50" data-id="element-251"></div>
              <img src="https://cdn.magicpatterns.com/uploads/2gaS1gs71GsHZTy4Fj1Epy/pasted-image.png" alt="Micro y Macro Economía 360 Pack" className="relative w-full h-auto rounded-2xl shadow-2xl border-4 border-white/30 transform hover:scale-[1.02] transition-transform duration-500" data-id="element-252" />
            </div>

            <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-blue-100 mb-8 md:mb-10 font-light leading-relaxed max-w-4xl mx-auto px-2" data-id="element-253">
              Todo el año de micro y macro economía resuelto para enseñar con
              claridad, seguridad y sentido real{' '}
              <span className="font-bold text-white border-b-4 border-amber-400" data-id="element-254">
                sin planificar nada desde cero
              </span>
            </p>

            <div className="flex justify-center" data-id="element-255">
              <CTAButton size="large" text="Quiero mis clases listas →" data-id="element-256" />
            </div>
          </div>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white" data-id="element-257">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-258">
          <div className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl border-l-8 border-rose-500" data-id="element-259">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8" data-id="element-260">
              Si sos docente de nivel secundario y alguna vez pensaste:
            </h2>
            <ul className="space-y-5 mb-8" data-id="element-261">
              <li className="flex items-start gap-4" data-id="element-262">
                <AlertTriangle className="text-rose-500 w-7 h-7 flex-shrink-0 mt-1" data-id="element-263" />
                <span className="text-xl text-gray-700 italic" data-id="element-264">
                  "Mañana tengo que explicar inflación… y no sé cómo hacerlo
                  simple"
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-265">
                <AlertTriangle className="text-rose-500 w-7 h-7 flex-shrink-0 mt-1" data-id="element-266" />
                <span className="text-xl text-gray-700 italic" data-id="element-267">
                  "Otra vez voy a improvisar"
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-268">
                <AlertTriangle className="text-rose-500 w-7 h-7 flex-shrink-0 mt-1" data-id="element-269" />
                <span className="text-xl text-gray-700 italic" data-id="element-270">
                  "Esto en el libro suena bien, pero en el aula no funciona"
                </span>
              </li>
            </ul>
            <p className="text-2xl font-bold text-gray-900" data-id="element-271">Seguí leyendo.</p>
          </div>

          <div className="mt-16 space-y-8 text-lg text-gray-700 leading-relaxed" data-id="element-272">
            <p className="text-xl" data-id="element-273">
              No porque te falte capacidad. No porque no sepas economía. Sino
              porque{' '}
              <span className="font-bold bg-amber-100 px-2 py-1 rounded" data-id="element-274">
                nadie te dio una herramienta pensada para tu realidad docente.
              </span>
            </p>

            <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-275">
              <div data-id="element-276">
                <h3 className="text-3xl font-bold text-indigo-900 mb-6" data-id="element-277">
                  El problema no es la economía. El problema es cómo nos
                  obligaron a enseñarla.
                </h3>
                <p className="mb-6 text-xl" data-id="element-278">La economía está en todos lados:</p>
                <ul className="space-y-3 mb-6" data-id="element-279">
                  <li className="flex items-center gap-3 text-lg" data-id="element-280">
                    <CheckCircle className="w-6 h-6 text-blue-600" data-id="element-281" /> En el
                    precio del colectivo
                  </li>
                  <li className="flex items-center gap-3 text-lg" data-id="element-282">
                    <CheckCircle className="w-6 h-6 text-blue-600" data-id="element-283" /> En el
                    sueldo que no alcanza
                  </li>
                  <li className="flex items-center gap-3 text-lg" data-id="element-284">
                    <CheckCircle className="w-6 h-6 text-blue-600" data-id="element-285" /> En la
                    inflación que aparece en las noticias
                  </li>
                  <li className="flex items-center gap-3 text-lg" data-id="element-286">
                    <CheckCircle className="w-6 h-6 text-blue-600" data-id="element-287" /> En las
                    decisiones que toman las familias todos los días
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-slate-100 to-slate-50 p-8 rounded-2xl shadow-lg" data-id="element-288">
                <p className="font-bold mb-6 text-gray-900 text-xl" data-id="element-289">
                  Y sin embargo… En el aula pasa esto:
                </p>
                <ul className="space-y-4" data-id="element-290">
                  <li className="flex items-center gap-3 text-gray-700 text-lg" data-id="element-291">
                    <div className="w-3 h-3 bg-rose-400 rounded-full" data-id="element-292"></div>
                    Alumnos desconectados
                  </li>
                  <li className="flex items-center gap-3 text-gray-700 text-lg" data-id="element-293">
                    <div className="w-3 h-3 bg-rose-400 rounded-full" data-id="element-294"></div>
                    Miradas perdidas
                  </li>
                  <li className="flex items-center gap-3 text-gray-700 text-lg" data-id="element-295">
                    <div className="w-3 h-3 bg-rose-400 rounded-full" data-id="element-296"></div>
                    Celulares bajo el banco
                  </li>
                  <li className="flex items-center gap-3 text-gray-700 font-bold italic text-lg" data-id="element-297">
                    "Profe… ¿esto para qué sirve?"
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-10 rounded-2xl border-2 border-blue-200 mt-10 shadow-lg" data-id="element-298">
              <h3 className="text-2xl font-bold text-indigo-900 mb-6 flex items-center gap-3" data-id="element-299">
                <Clock className="w-8 h-8" data-id="element-300" />
                La trampa silenciosa del docente de economía
              </h3>
              <p className="mb-6 text-xl" data-id="element-301">
                Llegás a casa cansado. Terminás de corregir. Y todavía te queda:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-302">
                <div className="bg-white p-4 rounded-xl shadow-md text-center font-medium" data-id="element-303">
                  Armar la próxima clase
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md text-center font-medium" data-id="element-304">
                  Buscar ejemplos
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md text-center font-medium" data-id="element-305">
                  Adaptar el contenido
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md text-center font-medium" data-id="element-306">
                  Traducir el lenguaje
                </div>
              </div>
              <p className="font-bold text-center text-indigo-900 text-xl" data-id="element-307">
                Horas de trabajo invisible. Horas que nadie ve. Horas que muchas
                veces no se notan en el aula.
              </p>
            </div>

            <div className="text-center mt-12" data-id="element-308">
              <CTAButton text="Quiero terminar con esto hoy →" data-id="element-309" />
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-white" data-id="element-310">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-311">
          <div className="text-center mb-20" data-id="element-312">
            <span className="text-indigo-600 font-bold tracking-widest uppercase text-base" data-id="element-313">
              La Solución
            </span>
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mt-4 mb-8 leading-tight" data-id="element-314">
              Acá aparece el eslabón perdido
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10 mb-20" data-id="element-315">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-10 rounded-3xl shadow-xl border-2 border-blue-100 text-center hover:transform hover:-translate-y-3 transition-all duration-500 group" data-id="element-316">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-8 text-white shadow-lg group-hover:scale-110 transition-transform" data-id="element-317">
                <BookOpen className="w-10 h-10" data-id="element-318" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-319">
                Pedagogía Aplicada
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-320">
                No es solo información. Es un sistema que transforma la teoría
                del libro en realidad del aula.
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-10 rounded-3xl shadow-xl border-2 border-blue-100 text-center hover:transform hover:-translate-y-3 transition-all duration-500 group" data-id="element-321">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-8 text-white shadow-lg group-hover:scale-110 transition-transform" data-id="element-322">
                <ShieldCheck className="w-10 h-10" data-id="element-323" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-324">
                Seguridad Total
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-325">
                Llegás con todo listo. Sabés exactamente qué explicar y cómo
                hacerlo.
              </p>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-10 rounded-3xl shadow-xl border-2 border-indigo-100 text-center hover:transform hover:-translate-y-3 transition-all duration-500 group" data-id="element-326">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-8 text-white shadow-lg group-hover:scale-110 transition-transform" data-id="element-327">
                <Users className="w-10 h-10" data-id="element-328" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-329">
                Conexión Real
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-330">
                Un traductor simultáneo entre la economía académica y el
                lenguaje del alumno.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white rounded-3xl p-10 md:p-16 shadow-2xl overflow-hidden relative" data-id="element-331">
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20" data-id="element-332"></div>

            <div className="relative z-10 grid md:grid-cols-2 gap-16 items-center" data-id="element-333">
              <div data-id="element-334">
                <h3 className="text-4xl font-bold mb-8" data-id="element-335">
                  ¿Qué es Micro y Macro Economía{' '}
                  <span className="text-amber-400" data-id="element-336">360</span>?
                </h3>
                <p className="text-xl text-blue-100 mb-8 leading-relaxed" data-id="element-337">
                  Es un pack completo de clases listas para usar, creado
                  exclusivamente para docentes de nivel secundario que dictan:
                </p>
                <ul className="grid grid-cols-2 gap-5 mb-10" data-id="element-338">
                  <li className="flex items-center gap-3 text-lg" data-id="element-339">
                    <CheckCircle className="text-amber-400 w-6 h-6" data-id="element-340" /> Economía
                  </li>
                  <li className="flex items-center gap-3 text-lg" data-id="element-341">
                    <CheckCircle className="text-amber-400 w-6 h-6" data-id="element-342" />
                    Microeconomía
                  </li>
                  <li className="flex items-center gap-3 text-lg" data-id="element-343">
                    <CheckCircle className="text-amber-400 w-6 h-6" data-id="element-344" />
                    Macroeconomía
                  </li>
                  <li className="flex items-center gap-3 text-lg" data-id="element-345">
                    <CheckCircle className="text-amber-400 w-6 h-6" data-id="element-346" /> Economía
                    política
                  </li>
                  <li className="flex items-center gap-3 text-lg" data-id="element-347">
                    <CheckCircle className="text-amber-400 w-6 h-6" data-id="element-348" /> Ciencias
                    sociales
                  </li>
                </ul>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border-l-4 border-amber-400 mb-10" data-id="element-349">
                  <p className="font-bold text-xl" data-id="element-350">
                    Especialmente pensado para 4º, 5º y 6º año.
                  </p>
                </div>
                <CTAButton text="Quiero acceder al material ahora →" variant="secondary" data-id="element-351" />
              </div>
              <div className="relative" data-id="element-352">
                <img src="https://cdn.magicpatterns.com/uploads/bBC9Auzi61snSbMRvkPD6b/conver_micro_y_macro_(1).png" alt="Pack Preview" className="w-full rounded-2xl shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-353" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features List */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white" data-id="element-354">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-355">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-900" data-id="element-356">
            ¿Qué incluye este pack?
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-357">
            {[{
            title: 'Clases estructuradas',
            desc: 'Listas para aplicar en el aula sin improvisar.'
          }, {
            title: 'Secuencia lógica y progresiva',
            desc: 'Un orden que tiene sentido para el alumno.'
          }, {
            title: 'Micro y Macro integradas',
            desc: 'Todo el contenido curricular cubierto.'
          }, {
            title: 'Ejemplos reales y actuales',
            desc: 'Casos que los chicos ven en su día a día.'
          }, {
            title: 'Lenguaje claro y didáctico',
            desc: "Explicaciones que 'bajan' la teoría a tierra."
          }, {
            title: 'Material listo para usar',
            desc: 'Sin armar desde cero. Sin perder horas.'
          }].map((item, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-lg border-2 border-slate-100 flex items-start gap-5 hover:shadow-xl transition-shadow" data-id="element-358">
                <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-3 rounded-full" data-id="element-359">
                  <CheckCircle className="w-7 h-7 text-indigo-600" data-id="element-360" />
                </div>
                <div data-id="element-361">
                  <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-362">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-lg" data-id="element-363">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Transformation Comparison */}
      <section className="py-24 bg-white" data-id="element-364">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-365">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-20 text-gray-900" data-id="element-366">
            Cómo cambia tu día a día
          </h2>

          <div className="grid md:grid-cols-2 gap-12 md:gap-20" data-id="element-367">
            {/* Before */}
            <div className="bg-gradient-to-br from-rose-50 to-red-50 p-10 rounded-3xl border-2 border-rose-200 relative shadow-xl" data-id="element-368">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-rose-500 to-red-500 text-white px-10 py-3 rounded-full font-bold shadow-xl text-lg" data-id="element-369">
                ANTES
              </div>
              <ul className="space-y-7 mt-8" data-id="element-370">
                <li className="flex items-center gap-5 text-gray-800 text-lg" data-id="element-371">
                  <div className="w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 font-bold text-xl flex-shrink-0" data-id="element-372">
                    ✕
                  </div>
                  Llegabas a clase con dudas
                </li>
                <li className="flex items-center gap-5 text-gray-800 text-lg" data-id="element-373">
                  <div className="w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 font-bold text-xl flex-shrink-0" data-id="element-374">
                    ✕
                  </div>
                  Improvisabas ejemplos
                </li>
                <li className="flex items-center gap-5 text-gray-800 text-lg" data-id="element-375">
                  <div className="w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 font-bold text-xl flex-shrink-0" data-id="element-376">
                    ✕
                  </div>
                  Veías caras de confusión
                </li>
                <li className="flex items-center gap-5 text-gray-800 text-lg" data-id="element-377">
                  <div className="w-10 h-10 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 font-bold text-xl flex-shrink-0" data-id="element-378">
                    ✕
                  </div>
                  Sentías inseguridad
                </li>
              </ul>
            </div>

            {/* After */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-10 rounded-3xl border-2 border-blue-200 relative shadow-2xl transform scale-105" data-id="element-379">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-10 py-3 rounded-full font-bold shadow-xl text-lg" data-id="element-380">
                AHORA
              </div>
              <ul className="space-y-7 mt-8" data-id="element-381">
                <li className="flex items-center gap-5 text-gray-900 font-semibold text-lg" data-id="element-382">
                  <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-xl flex-shrink-0" data-id="element-383">
                    ✓
                  </div>
                  Llegás con todo listo
                </li>
                <li className="flex items-center gap-5 text-gray-900 font-semibold text-lg" data-id="element-384">
                  <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-xl flex-shrink-0" data-id="element-385">
                    ✓
                  </div>
                  Sabés exactamente qué explicar
                </li>
                <li className="flex items-center gap-5 text-gray-900 font-semibold text-lg" data-id="element-386">
                  <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-xl flex-shrink-0" data-id="element-387">
                    ✓
                  </div>
                  Los alumnos entienden y participan
                </li>
                <li className="flex items-center gap-5 text-gray-900 font-semibold text-lg" data-id="element-388">
                  <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-xl flex-shrink-0" data-id="element-389">
                    ✓
                  </div>
                  Volvés a sentirte seguro y profesional
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-20 text-center max-w-4xl mx-auto" data-id="element-390">
            <p className="text-3xl font-serif italic text-gray-700 mb-10 leading-relaxed" data-id="element-391">
              "Podés dar un año completo de Micro y Macro Economía con clases
              claras, actuales y ordenadas sin volver a planificar desde cero"
            </p>
            <CTAButton text="Sí, quiero enseñar con claridad →" size="large" data-id="element-392" />
          </div>
        </div>
      </section>

      <BonusSection data-id="element-393" />

      <PurchaseSection data-id="element-394" />

      <TestimonialSection data-id="element-395" />

      <FAQSection data-id="element-396" />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-16 border-t border-slate-800" data-id="element-397">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-398">
          <p className="mb-4 text-lg" data-id="element-399">
            © 2026 Micro y Macro Economía 360. Todos los derechos reservados.
          </p>
          <p className="text-sm text-slate-500" data-id="element-400">
            Este sitio no forma parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>;
}